import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { useLocation } from 'react-use';
import { type Store } from 'redux';

import getStore, { initStore } from '$store';
import App from '~components/app/App';
import CustomJsExecutor from '~components/CustomJsExecutor';
import { InitializationLoader } from '~components/InitializationLoader';
import { redirectToLogin, verifyLoginStatus } from '~services/api/calls/user';
import { useAppLogin } from '~services/env';
import { getLangFromLocation } from '~services/location-helpers';
import log from '~services/log';
import { getSiteInfo } from '~services/siteInfo';
import appendCss from '~utils/appendCss';
import { refreshFavicon } from '~utils/favicon';
import ResponsiveHelper from './components/ResponsiveHelper';
import { UserActions } from './store/actions';
import {
  getConfigAnonymousPages,
  getConfigBackendUrl,
  getConfigCssPatcherUrl,
  getConfigCssUrl,
  getConfigIsPublicMode,
  getConfigSiteAccess,
} from './store/getter';
import {
  selectConfigBackendUrl,
  selectSessionName,
  selectSessionServerSession,
  selectUserId,
  selectUserIsValid,
} from './store/selectors';
import { init } from './store/thunks/init';

export const Auth = () => {
  const [isReadyForInit, setIsReadyForInit] = useState(false);
  const [hasInitFailed, setHasInitFailed] = useState(false);
  const [isAuthUser, setIsAuthUser] = useState(false);

  const location = useLocation();

  const requestOauthToken = () => {
    if (window.ebm?.instances?.oauthTokenManager) {
      const { oauthTokenManager } = window.ebm.instances;
      oauthTokenManager.getAccessToken();
    }
  };

  const isUserValid = (state: any): boolean => {
    const userId = selectUserId(state);
    if (userId) {
      const userValid = selectUserIsValid(state);
      if (userValid !== null && userValid === false) {
        log('User profile not valid - redirecting to the edit form', { userId, isValid: userValid }, 'error');
        const userEditUrl = `${selectConfigBackendUrl(state)}/user/${userId}/edit`;
        window.location.href = userEditUrl;
        return false;
      } else {
        return true;
      }
    } else {
      throw new Error('No userId');
    }
  };

  const triggerSetConfigUserInfo = (store: Store, user: any) => {
    setTimeout(() => {
      store.dispatch(UserActions.set(user));
    });
  };

  const triggerLoggedIn = store => {
    setTimeout(() => {
      store.dispatch(init());
    });
  };

  const initFailed = () => {
    setIsReadyForInit(false);
    setHasInitFailed(true);
  };

  useEffect(() => {
    const initializeApp = async () => {
      log('Request Application context…');
      try {
        // Read current language (in url /en/ or /de/)
        const lang = getLangFromLocation();

        const siteInfo = await getSiteInfo();
        log('Application site info loaded! Initializing...', siteInfo);

        // Re-Create redux store with siteinfo data
        const store = initStore(lang, siteInfo);

        // Apply css theme
        const css = getConfigCssUrl();
        log('Apply CSS...', css);
        appendCss(css);

        // Apply css patcher
        const csspatcher = getConfigCssPatcherUrl();
        if (csspatcher !== '') {
          log('Apply CSS patcher...', csspatcher);
          appendCss(csspatcher);
        }

        // Dynamic Favicon
        const siteAccess = getConfigSiteAccess();
        if (siteAccess) {
          refreshFavicon(siteAccess.url, siteAccess.theme);
        }

        // indicates if the app should use it's own build in login
        if (useAppLogin) {
          const state = store.getState();
          const serverSession = selectSessionServerSession(state);
          const userId = selectUserId(state);
          if (userId === 0 && serverSession !== null && serverSession.current_user.uid !== 0) {
            // user data is set in session (state cookie) but not yet in the app config
            log('Set userInfo in config from session', serverSession.current_user);
            triggerSetConfigUserInfo(store, serverSession.current_user);
          }
          if (selectSessionName(state)) {
            triggerLoggedIn(store);
          }
          setIsReadyForInit(true);
        } else {
          if (!getConfigIsPublicMode() && !isAuthUser && isAnonymousPage(location.pathname)) {
            log('App is private, but pathname is public!');
            log('Start the app!');
            triggerLoggedIn(store);
            setIsReadyForInit(true);
          } else if (!getConfigIsPublicMode()) {
            log('App is in private mode!', null, 'warning');
            log('Validate login status...');
            const isLoginVerified = await verifyLoginStatus();
            if (isLoginVerified) {
              log('Login status is valid!');
              const validUser = isUserValid(store.getState());
              setIsAuthUser(true);
              if (validUser) {
                log('Start the app!');
                triggerLoggedIn(store);
                setIsReadyForInit(true);
              }
            } else {
              log('Login status is invalid!', { isLoginVerified }, 'warning');
              redirectToLogin(getConfigBackendUrl());
            }
          } else {
            log('App is not private!');
            log('Start the app!');
            triggerLoggedIn(store);
            setIsReadyForInit(true);
          }
        }
      } catch (e) {
        log('Error initializing!', e, 'error');
        initFailed();
      }
    };

    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isReadyForInit) {
      if (isAuthUser) requestOauthToken();
    }
  }, [isReadyForInit, isAuthUser]);

  // helper to check if given page is public
  const isAnonymousPage = path => {
    const anonymousPages = getConfigAnonymousPages();
    if (!anonymousPages) return false;
    const filteredPaths = anonymousPages.filter(option => {
      return option.substring(option.lastIndexOf('/')) === path.substring(path.lastIndexOf('/'));
    });
    return filteredPaths.length > 0;
  };

  // check if login is required
  useEffect(() => {
    if (!isAuthUser && !getConfigIsPublicMode() && !isAnonymousPage(location.pathname)) {
      const backendUrl = getConfigBackendUrl();
      if (backendUrl) redirectToLogin(backendUrl);
    }
  }, [isAuthUser, location]);

  if (isReadyForInit) {
    return (
      <Provider store={getStore()}>
        <App />
        <CustomJsExecutor />
        <ResponsiveHelper />
      </Provider>
    );
  } else if (hasInitFailed) {
    return <p>Loading configuration failed!</p>;
  } else {
    return <InitializationLoader />;
  }
};
